/**
 * The project-wide punch list: what it shows, in what order, and who each
 * item is for (2026-09-22).
 *
 * Extracted from the view before the view existed, per CLAUDE.md — the three
 * decisions here are all ones a 300-line render function would hide:
 *
 *  - an item's vendor is a contact row, but items written before 0040 have
 *    only free text in `assignee`, and BOTH have to keep showing;
 *  - "overdue" is a date-only comparison, so it goes through isPastDue();
 *  - open work sorts above finished work, whatever its dates say.
 */

import { isPastDue } from '@/lib/dates';
import { fmtDate } from '@/lib/dom';
import type { PunchItemRow, DesignerContactRow, RoomRow } from '@/types/database';

/** The fields this module needs. Narrow so portal payloads could pass too. */
export type PunchFields = Pick<
  PunchItemRow, 'id' | 'room_id' | 'description' | 'assignee' | 'designer_contact_id' | 'due_date' | 'complete' | 'sort_order'
>;

/**
 * Who an item is for, or null when nobody is named.
 *
 * The contact link wins; `assignee` is what items written before 0040 have,
 * and dropping it would blank out every punch item already on file.
 */
export function punchVendorName(
  item: Pick<PunchFields, 'assignee' | 'designer_contact_id'>,
  contacts: Pick<DesignerContactRow, 'id' | 'name'>[],
): string | null {
  if (item.designer_contact_id) {
    const c = contacts.find((x) => x.id === item.designer_contact_id);
    // A contact deleted since (the column is `on delete set null`, so this is
    // only a list that hasn't been reloaded) still reads better as the typed
    // name than as nothing at all.
    if (c) return c.name;
  }
  const typed = (item.assignee ?? '').trim();
  return typed === '' ? null : typed;
}

/**
 * Past its due date and still open.
 *
 * `due_date` is a Postgres `date`, so this goes through isPastDue — the naive
 * `new Date(due) < now` reads as yesterday in every timezone west of UTC, and
 * would flag items due TODAY as overdue. A completed item is never overdue:
 * the work is done, whenever it happened.
 */
export function isPunchOverdue(item: Pick<PunchFields, 'complete' | 'due_date'>, today: Date = new Date()): boolean {
  if (item.complete) return false;
  return isPastDue(item.due_date, today);
}

/**
 * What a punch item with no room reads as (0048).
 *
 * "No room" rather than "Whole house" — Mick's choice, 2026-09-30. It says
 * only what is true: no room is set. It is NOT sent to a contractor; see
 * punchMessage.
 */
export const PUNCH_NO_ROOM = 'No room';

export interface PunchFilter {
  /** Room id, 'all', or 'none' for items in no room (0048). */
  room: string;
  /** Contact id, 'all', or 'none' for items with nobody assigned. */
  vendor: string;
  /** 'open' hides completed items; 'all' shows everything. */
  show: 'open' | 'all';
}

export const DEFAULT_PUNCH_FILTER: PunchFilter = { room: 'all', vendor: 'all', show: 'open' };

export function filterPunch<T extends PunchFields>(items: T[], f: PunchFilter): T[] {
  return items.filter((i) => {
    if (f.show === 'open' && i.complete) return false;
    if (f.room === 'none' && i.room_id !== null) return false;
    if (f.room !== 'all' && f.room !== 'none' && i.room_id !== f.room) return false;
    if (f.vendor === 'none' && (i.designer_contact_id || (i.assignee ?? '').trim() !== '')) return false;
    if (f.vendor !== 'all' && f.vendor !== 'none' && i.designer_contact_id !== f.vendor) return false;
    return true;
  });
}

/**
 * Open items first, then soonest due, then undated, then the room's own order.
 *
 * Undated items sort AFTER dated ones rather than first: a punch item with no
 * date is one nobody has committed to, and it should not push a dated one down
 * the page. Returns a new array — the caller's list is the page's data.
 */
export function sortPunch<T extends PunchFields>(items: T[], roomOrder: Pick<RoomRow, 'id'>[] = []): T[] {
  const roomIndex = new Map(roomOrder.map((r, i) => [r.id, i]));
  return [...items].sort((a, b) => {
    if (a.complete !== b.complete) return a.complete ? 1 : -1;
    if (a.due_date !== b.due_date) {
      if (!a.due_date) return 1;
      if (!b.due_date) return -1;
      return a.due_date < b.due_date ? -1 : 1;
    }
    // An item in no room sorts with one whose room is unknown: after every
    // located item, for the same reason an undated one sorts after a dated
    // one. It is still real work, and the due date above already ordered it.
    const ra = roomRank(a.room_id, roomIndex);
    const rb = roomRank(b.room_id, roomIndex);
    if (ra !== rb) return ra - rb;
    return a.sort_order - b.sort_order;
  });
}

function roomRank(id: string | null, index: Map<string, number>): number {
  if (id === null) return Number.MAX_SAFE_INTEGER;
  return index.get(id) ?? Number.MAX_SAFE_INTEGER;
}

/**
 * The message sent to a vendor about their open punch items.
 *
 * Text only — no link. A punch list is the designer's working list, and the
 * contractor portal (which has its own link) shows scheduled work, not this.
 * Sending "here is what's outstanding for you" in plain words is the whole
 * ask; anything more would promise a screen that doesn't exist.
 *
 * Numbered rather than bulleted: a contractor reading it on a phone can reply
 * "3 and 4 are done".
 */
export function punchMessage(input: {
  vendorName: string | null;
  projectName: string;
  items: Array<{ description: string; due_date?: string | null; roomName?: string | null }>;
}): string {
  const who = (input.vendorName ?? '').trim();
  const hello = who ? `Hi ${who.split(' ')[0]} — ` : '';
  const lines = input.items.map((it, i) => {
    // An item with no room contributes no location at all (0048). Texting a
    // contractor "No room" tells them nothing a missing room didn't already,
    // and reads like a bug in the message.
    const where = (it.roomName ?? '').trim();
    const due = (it.due_date ?? '').trim();
    const detail = [where, due ? `due ${fmtDate(due)}` : ''].filter(Boolean).join(', ');
    return `${i + 1}. ${it.description}${detail ? ` (${detail})` : ''}`;
  });
  const count = input.items.length === 1 ? 'one punch item' : `${input.items.length} punch items`;
  return `${hello}${count} for ${input.projectName}:\n${lines.join('\n')}`;
}

export interface PunchCounts {
  open: number;
  done: number;
  overdue: number;
  total: number;
}

/** Counted over ALL of a project's items, so the header doesn't move when a filter does. */
export function punchCounts(items: Pick<PunchFields, 'complete' | 'due_date'>[], today: Date = new Date()): PunchCounts {
  const open = items.filter((i) => !i.complete).length;
  return {
    open,
    done: items.length - open,
    overdue: items.filter((i) => isPunchOverdue(i, today)).length,
    total: items.length,
  };
}

/** "4 open · 1 overdue · 7 done", or the empty-state line. */
export function punchSummary(c: PunchCounts): string {
  if (c.total === 0) return 'No punch items yet.';
  const parts = [`${c.open} open`];
  if (c.overdue > 0) parts.push(`${c.overdue} overdue`);
  if (c.done > 0) parts.push(`${c.done} done`);
  return parts.join(' · ');
}
